import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS, ROUTES } from '../../constants';
import { Order, PaymentRecord, ShipmentTimeline } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/pricing';
import { resolveImageUrl } from '../../utils/image';

const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'] as const;
const PAYMENT_STATUSES = ['pending', 'paid', 'failed'] as const;

const statusColor = (status: string): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'processing':
      return 'info';
    case 'shipped':
      return 'info';
    case 'delivered':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const AdminOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [shipBusy, setShipBusy] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [timeline, setTimeline] = useState<ShipmentTimeline | null>(null);
  const [openRawEvent, setOpenRawEvent] = useState<string | null>(null);

  const loadAudit = useCallback(async () => {
    // Best-effort side panels — the order itself is the primary load
    apiClient
      .get(`/payments/orders/${id}`)
      .then((res) => setPayments(res.data as PaymentRecord[]))
      .catch(() => setPayments([]));
    apiClient
      .get(`/shipping/orders/${id}/tracking`)
      .then((res) => setTimeline(res.data as ShipmentTimeline))
      .catch(() => setTimeline(null));
  }, [id]);

  const createShipment = async () => {
    setShipBusy(true);
    setShipError(null);
    try {
      const response = await apiClient.post(`/shipping/orders/${id}/shipment`);
      setOrder(response.data as Order);
      loadAudit();
    } catch (err) {
      setShipError(extractApiErrorMessage(err, 'Failed to create shipment'));
    } finally {
      setShipBusy(false);
    }
  };

  const refreshShipment = async () => {
    setShipBusy(true);
    setShipError(null);
    try {
      const response = await apiClient.post(`/shipping/orders/${id}/shipment/refresh`);
      setOrder(response.data as Order);
      loadAudit();
    } catch (err) {
      setShipError(extractApiErrorMessage(err, 'Failed to refresh tracking'));
    } finally {
      setShipBusy(false);
    }
  };

  const openLabel = async () => {
    setShipBusy(true);
    setShipError(null);
    try {
      const response = await apiClient.get(`/shipping/orders/${id}/shipment/label`);
      const link =
        response.data?.packages?.[0]?.pdf_download_link || response.data?.pdf_download_link;
      if (link) {
        window.open(link, '_blank');
      } else {
        setShipError('Delhivery did not return a label link yet — try again in a minute.');
      }
    } catch (err) {
      setShipError(extractApiErrorMessage(err, 'Failed to fetch packing slip'));
    } finally {
      setShipBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.ORDERS}/${id}`);
      setOrder(response.data as Order);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load order'));
    }
  }, [id]);

  useEffect(() => {
    load();
    loadAudit();
  }, [load, loadAudit]);

  const updateStatus = async (status: string) => {
    setUpdating(true);
    try {
      const response = await apiClient.patch(`${API_ENDPOINTS.ORDERS}/${id}/status`, { status });
      setOrder(response.data as Order);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update status'));
    } finally {
      setUpdating(false);
    }
  };

  const updatePayment = async (paymentStatus: string) => {
    setUpdating(true);
    try {
      const response = await apiClient.patch(`${API_ENDPOINTS.ORDERS}/${id}/payment`, {
        paymentStatus
      });
      setOrder(response.data as Order);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update payment status'));
    } finally {
      setUpdating(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!order) {
    return (
      <Box>
        <Skeleton width={300} height={40} />
        <Skeleton variant="rounded" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const subtotal =
    order.totalAmount +
    (order.discountAmount || 0) +
    (order.paymentDiscount || 0) -
    (order.shippingCharge || 0);

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Button
        component={RouterLink}
        to={`${ROUTES.ADMIN}/orders`}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        All orders
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Order #{order._id.slice(-8).toUpperCase()}
        </Typography>
        <Chip size="small" color={statusColor(order.status)} label={order.status} />
        <Chip
          size="small"
          variant="outlined"
          color={order.paymentStatus === 'paid' ? 'success' : order.paymentStatus === 'failed' ? 'error' : 'warning'}
          label={`payment: ${order.paymentStatus}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 3 }}>
        <Box>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Items
            </Typography>
            {order.items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Box
                  component="img"
                  src={resolveImageUrl(item.product?.images?.[0] || '')}
                  alt=""
                  sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {item.product?.name || 'Product removed'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(item.price)} × {item.quantity}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(item.price * item.quantity)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
            </Box>
            {Boolean(order.discountAmount) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="success.main">
                  Discount {order.couponCode ? `(${order.couponCode})` : ''}
                </Typography>
                <Typography variant="body2" color="success.main">
                  -{formatCurrency(order.discountAmount || 0)}
                </Typography>
              </Box>
            )}
            {Boolean(order.paymentDiscount) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="success.main">
                  Online payment discount
                </Typography>
                <Typography variant="body2" color="success.main">
                  -{formatCurrency(order.paymentDiscount || 0)}
                </Typography>
              </Box>
            )}
            {Boolean(order.shippingCharge) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Delivery
                </Typography>
                <Typography variant="body2">{formatCurrency(order.shippingCharge || 0)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="subtitle2">Total</Typography>
              <Typography variant="subtitle2">{formatCurrency(order.totalAmount)}</Typography>
            </Box>
          </Paper>

          {order.notes && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Customer notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {order.notes}
              </Typography>
            </Paper>
          )}
        </Box>

        <Box>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Update
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Order status"
              value={order.status}
              disabled={updating}
              onChange={(e) => updateStatus(e.target.value)}
              sx={{ mb: 2 }}
            >
              {ORDER_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment status"
              value={order.paymentStatus}
              disabled={updating}
              onChange={(e) => updatePayment(e.target.value)}
            >
              {PAYMENT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Status changes email the customer automatically.
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Customer
            </Typography>
            <Typography variant="body2">{order.customerName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customerEmail}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {order.customerPhone}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Ship to
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.shippingAddress.street}
              <br />
              {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
              {order.shippingAddress.zipCode}
              <br />
              {order.shippingAddress.country}
            </Typography>
          </Paper>

          {order.attribution && (order.attribution.src || order.attribution.utmSource) && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Source
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  label={order.attribution.src || order.attribution.utmSource}
                />
                {order.attribution.utmCampaign && (
                  <Chip size="small" variant="outlined" label={`campaign: ${order.attribution.utmCampaign}`} />
                )}
                {order.attribution.utmMedium && (
                  <Chip size="small" variant="outlined" label={`medium: ${order.attribution.utmMedium}`} />
                )}
              </Box>
              {order.attribution.landingPage && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  Landed on {order.attribution.landingPage}
                </Typography>
              )}
              {order.attribution.referrer && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  Referrer: {order.attribution.referrer}
                </Typography>
              )}
              {order.attribution.capturedAt && (
                <Typography variant="caption" color="text.secondary">
                  First seen {new Date(order.attribution.capturedAt).toLocaleString()}
                </Typography>
              )}
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Shipping (Delhivery)
            </Typography>
            {shipError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setShipError(null)}>
                {shipError}
              </Alert>
            )}
            {order.shipment?.waybill ? (
              <>
                <Typography variant="body2">
                  Waybill: <strong>{order.shipment.waybill}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {order.shipment.lastScan || order.shipment.status || 'Manifested'}
                </Typography>
                {order.shipment.lastScanAt && (
                  <Typography variant="caption" color="text.secondary">
                    Last scan: {new Date(order.shipment.lastScanAt).toLocaleString()}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    href={`https://www.delhivery.com/track-v2/package/${order.shipment.waybill}`}
                    target="_blank"
                  >
                    Track
                  </Button>
                  <Button size="small" disabled={shipBusy} onClick={refreshShipment}>
                    Refresh status
                  </Button>
                  <Button size="small" disabled={shipBusy} onClick={openLabel}>
                    Packing slip
                  </Button>
                </Box>
                {timeline && timeline.scans.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                    {[...timeline.scans].reverse().map((scan, index) => (
                      <Box
                        key={`${scan.at}-${index}`}
                        sx={{
                          py: 0.75,
                          borderBottom: '1px dashed rgba(0,0,0,0.08)',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: index === 0 ? 600 : 400 }}>
                            {scan.status}
                          </Typography>
                          {scan.scanType && (
                            <Chip size="small" variant="outlined" label={scan.scanType} sx={{ height: 18 }} />
                          )}
                        </Box>
                        {scan.remarks && scan.remarks !== scan.status && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {scan.remarks}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {[scan.location, new Date(scan.at).toLocaleString()].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  No shipment yet. Creating one books a Delhivery pickup with{' '}
                  {order.paymentMethod === 'cod'
                    ? `COD collection of ${formatCurrency(order.totalAmount)}`
                    : 'prepaid delivery'}
                  .
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={shipBusy || order.status === 'cancelled'}
                  onClick={createShipment}
                >
                  {shipBusy ? 'Creating…' : 'Create Delhivery shipment'}
                </Button>
              </>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Payment
            </Typography>
            <Typography variant="body2">
              Method: {order.paymentMethod === 'cod' ? 'Cash on Delivery' : order.paymentMethod}
            </Typography>
            {order.payment?.transactionId && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Txn: {order.payment.transactionId}
              </Typography>
            )}
            {payments.length === 0 ? (
              order.payment?.state && (
                <Typography variant="body2" color="text.secondary">
                  Gateway state: {order.payment.state}
                </Typography>
              )
            ) : (
              payments.map((payment) => (
                <Box
                  key={payment._id}
                  sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.08)' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {payment.provider.toUpperCase()} · {formatCurrency(payment.amount)}
                    </Typography>
                    <Chip
                      size="small"
                      label={payment.status}
                      color={
                        payment.status === 'success'
                          ? 'success'
                          : payment.status === 'failed'
                            ? 'error'
                            : 'warning'
                      }
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  </Box>
                  {payment.providerOrderId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Gateway order: {payment.providerOrderId}
                    </Typography>
                  )}
                  {payment.providerTransactionId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Gateway txn: {payment.providerTransactionId}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    {payment.events.map((event, index) => {
                      const eventKey = `${payment._id}-${index}`;
                      return (
                        <Box key={eventKey} sx={{ py: 0.25 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            onClick={() =>
                              setOpenRawEvent(openRawEvent === eventKey ? null : eventKey)
                            }
                            sx={{ cursor: event.raw ? 'pointer' : 'default', display: 'block' }}
                          >
                            {new Date(event.at).toLocaleString()} · {event.type}
                            {event.state ? ` → ${event.state}` : ''}
                            {event.raw ? (openRawEvent === eventKey ? ' ▾' : ' ▸') : ''}
                          </Typography>
                          {Boolean(event.raw) && openRawEvent === eventKey && (
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                mt: 0.5,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'rgba(43,37,43,0.05)',
                                fontSize: 11,
                                overflowX: 'auto',
                                maxHeight: 200
                              }}
                            >
                              {JSON.stringify(event.raw, null, 2)}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminOrderDetailPage;
